<template>
    <div
        class="sticky-header tw:grid tw:grid-cols-12 tw:items-center"
        :class="[
            computedBackgroundClass,
            {
                'no-margin-top': noMarginTop,
                'no-margin-bottom': noMarginBottom,
                'no-padding-top': noPaddingTop,
            },
        ]"
    >
        <!-- Full overwrite slot -->
        <slot name="full-header">
            <div class="sticky-header__title">
                <h1 class="title tw:truncate">
                    <slot />
                </h1>

                <h2 v-if="$slots.subtitle" class="subtitle">
                    <slot name="subtitle" />
                </h2>
            </div>

            <div class="sticky-header__actions tw:w-full tw:h-full tw:flex tw:items-start tw:justify-end tw:pt-1">
                <button
                    v-if="$slots.actions"
                    type="button"
                    class="mobile-actions-button sticky-header__mobile-trigger tw:my-auto"
                    aria-label="More actions"
                    @click="showActionsDrawer = true"
                >
                    <i class="uil uil-ellipsis-v" />
                </button>

                <div v-if="!($isMobile || $isTablet)" class="sticky-header__desktop-actions tw:my-auto">
                    <slot name="actions" />
                </div>
            </div>

            <slot name="tabs" />

            <slot name="details" />
        </slot>
    </div>

    <!-- Mobile Actions Drawer -->
    <Drawer
        v-model:visible="showActionsDrawer"
        position="bottom"
        header="Actions"
        class="tw:h-6/8"
        :pt="{
            header: {
                class: 'actions-drawer-header',
            }
        }"
    >
        <div class="tw:grid tw:gap-2 tw:p-2">
            <div class="tw:w-full">
                <slot name="actions" />
            </div>
            <e-button
                class="tw:w-full"
                type="primary"
                outlined
                @click="showActionsDrawer = false"
            >
                Cancel
            </e-button>
        </div>
    </Drawer>
</template>

<script>
// Full class strings must stay literal so Tailwind v4 Oxide can emit the utilities.
const BACKGROUND_CLASSES = {
    surface: 'tw:bg-[var(--layout-content-ground)]',
    background: 'tw:bg-[var(--p-content-background)]',
};

export default {
    name: 'StickyHeader',

    props: {
        headerLeft: {
            default: '6',
            type: [String, Number],
        },
        headerRight: {
            default: '6',
            type: [String, Number],
        },
        hideBottomBorder: {
            default: false,
            type: Boolean,
        },
        noMarginBottom: {
            default: false,
            type: Boolean,
        },
        noMarginTop: {
            default: false,
            type: Boolean,
        },
        noPaddingTop: {
            default: false,
            type: Boolean,
        },
        transparent: {
            default: false,
            type: Boolean,
        },
        // 'surface' is a back-compat alias: it maps to --layout-content-ground (page ground),
        // not a --surface-* token. 'background' maps to --p-content-background.
        backgroundColor: {
            default: 'surface',
            type: String,
        },
    },

    data: () => ({
        showActionsDrawer: false,
    }),

    computed: {
        computedBackgroundClass() {
            if (this.transparent) {
                return '';
            }

            return this.backgroundColor === 'background' ? BACKGROUND_CLASSES.background : BACKGROUND_CLASSES.surface;
        },
    },
};
</script>

<style lang="scss">
@import 'resources/scss/toolbox.scss';
@import 'resources/scss/layout/_sass_variables.scss';

.sticky-header {
    top: -2rem; // Matches .layout-content padding to reach navbar when stuck
    z-index: 11;
    overflow: visible !important;
    position: sticky;
    position: -webkit-sticky;
    padding-top: 2rem; // Offset the negative top so content isn't hidden
    @include from($navbar-breakpoint) {
        margin-bottom: $main-padding-vertical !important;
        margin-top: calc(-#{$main-padding-vertical}) !important;
        padding-top: calc(2rem + #{$column-gap}); // 2rem offset + original padding
    }

    &__title {
        grid-column: span 4;
    }

    &__actions {
        grid-column: span 8;
    }

    &__desktop-actions {
        display: flex;
    }

    &__mobile-trigger {
        display: none;
    }

    @include until($breakpoint) {
        &__title {
            grid-column: span 10;
        }

        &__actions {
            grid-column: span 2;
        }

        &__desktop-actions {
            display: none;
        }

        &__mobile-trigger {
            display: flex;
        }
    }

    .subtitle {
        color: var(--text-color);
        margin-top: 0 !important;
    }

    .auto-save {
        justify-content: center;
        margin-left: 0;

        @include from($tablet) {
            justify-content: flex-end;
        }
    }

    &.no-margin-bottom {
        @include from($navbar-breakpoint) {
            margin-bottom: 0 !important;
        }
    }

    &.no-margin-top {
        @include from($navbar-breakpoint) {
            margin-top: 0 !important;
        }
    }

    &.no-padding-top {
        padding-top: 0 !important;
    }
}

.mobile-actions-button {
    background: var(--p-content-background);
    border: 1px solid var(--p-content-border-color);
    border-radius: var(--p-content-border-radius);
    height: 2.5rem;
    width: 2.5rem;
    cursor: pointer;
    color: var(--p-text-color);
    font-size: 1.5rem;
    transition: color 0.2s ease;

    &:hover {
        color: var(--p-primary-color);
    }

    &:focus {
        outline: 2px solid var(--p-primary-color);
        outline-offset: 2px;
        border-radius: 4px;
    }
}

.actions-drawer-header {
    padding-bottom: 0;
    border-bottom: 1px solid var(--p-content-border-color);
}
</style>
